import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs, Table, Tag, Descriptions, Empty, Spin, Card, Rate } from 'antd';
import { ShieldAlert, PenLine, StickyNote, TestTube } from 'lucide-react';
import Icons from '@/components/third-party/Icons';
import { useTheme } from '@/App';
import { get_batch_operation_details } from '@/utils/api';
import { resolveOperationExecutionStatus } from '@/utils/operationExecutionStatus';
import { fmt, yesNo, statusTagColor, personName, formatSeconds, TicketTag } from './helpers';

// ==============================|| OPERATION DETAIL PANEL (inline expand content) ||============================== //

const renderActualsTable = (actuals, extraCols = []) => (
  <Table
    size="small"
    dataSource={(actuals || []).map((a, i) => ({ ...a, key: a.actual_id ?? i }))}
    columns={[
      ...extraCols,
      { title: 'Stage', dataIndex: 'operation_stage', key: 'operation_stage', render: (v) => v || '—' },
      { title: 'Recorded By', dataIndex: 'recorded_by', key: 'recorded_by', render: personName },
      { title: 'Recorded On', dataIndex: 'created_on', key: 'created_on', render: fmt },
    ]}
    pagination={false}
    scroll={{ x: 'max-content' }}
    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No actuals recorded" /> }}
  />
);

const renderDeviationBlock = (deviation) => {
  if (!deviation) return null;
  return (
    <Card size="small" className="mt-2" style={{ borderLeft: '4px solid #dc2626' }}>
      <div className="flex flex-wrap gap-4 items-start">
        <Descriptions size="small" column={1} className="flex-1" style={{ minWidth: 260 }}>
          <Descriptions.Item label="Deviation Type">
            <Tag color="red">{deviation.deviation_type || '—'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Actual">{deviation.actual_value ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Expected">{deviation.expected_value ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Range">
            {deviation.min_value ?? '—'} – {deviation.max_value ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Action">
            <Tag color="orange">{deviation.action || '—'}</Tag>
          </Descriptions.Item>
        </Descriptions>
        {deviation.ticket && (
          <div className="flex-1" style={{ minWidth: 220 }}>
            <TicketTag ticket={deviation.ticket} />
          </div>
        )}
      </div>
    </Card>
  );
};

const OperationDetailPanel = ({ batchNumber, operationId }) => {
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!operationId) return;
    setLoading(true);
    setDetail(null);
    get_batch_operation_details(
      { batch_number: batchNumber, operation_id: operationId },
      (res) => {
        setDetail(res?.data || null);
        setLoading(false);
      }
    );
  }, [operationId, batchNumber]);

  const op = detail?.operation || {};
  const exec = detail?.execution || {};
  const ingredients = detail?.ingredients || [];
  const parameters = detail?.parameters || [];
  const sampling = detail?.sampling || [];
  const products = detail?.products || [];
  const instructions = detail?.instructions || [];
  const actionList = detail?.action_list || [];
  const lineClearances = detail?.line_clearances || [];
  const deviations = detail?.deviations || [];
  const durationDeviation = detail?.duration_deviation;
  const supervisorApproval = detail?.supervisor_approval;
  const recipeApprovals = detail?.recipe_approvals || [];
  const esignAudit = detail?.esign_audit || [];
  const reviews = detail?.reviews || [];
  const notes = detail?.notes || [];

  const execStatus = resolveOperationExecutionStatus(exec.status);

  const iconLabel = (icon, text) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {icon}
      {text}
    </span>
  );

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Spin size="large" tip="Loading operation details…" />
      </div>
    );
  }

  if (!detail) {
    return <Empty description="No details available for this operation" style={{ padding: 24 }} />;
  }

  const overviewTab = (
    <div className="space-y-2">
      <Card size="small" title="Operation">
        <Descriptions size="small" column={3}>
          <Descriptions.Item label="Code">{op.operation_code || '—'}</Descriptions.Item>
          <Descriptions.Item label="Number">{op.operation_number ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Name" span={2}>{op.operation_name || '—'}</Descriptions.Item>
          <Descriptions.Item label="Type">{op.operation_type || '—'}</Descriptions.Item>
          <Descriptions.Item label="Min / Max Duration">
            {op.min_duration ?? '—'} / {op.max_duration ?? '—'} min
          </Descriptions.Item>
          <Descriptions.Item label="Instruction" span={2}>{op.instruction || '—'}</Descriptions.Item>
          <Descriptions.Item label="Supervisor Approval Required">
            {yesNo(op.is_supervisor_approval_required)}
          </Descriptions.Item>
          <Descriptions.Item label="E-Sign Required">{yesNo(op.is_esign_required)}</Descriptions.Item>
          <Descriptions.Item label="Pass / Fail">{yesNo(op.is_pass_fail)}</Descriptions.Item>
          <Descriptions.Item label="Carry Forward">
            {yesNo(op.is_carry_forward_operation)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" title="Execution">
        <Descriptions size="small" column={3}>
          <Descriptions.Item label="Status">
            <Tag color={execStatus.style.tagColor}>{execStatus.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Machine">{exec.machine || '—'}</Descriptions.Item>
          <Descriptions.Item label="Scheduled Time">{fmt(exec.scheduled_time)}</Descriptions.Item>
          <Descriptions.Item label="Paused">{formatSeconds(exec.paused_seconds)}</Descriptions.Item>
          <Descriptions.Item label="Start Time">{fmt(exec.start_time)}</Descriptions.Item>
          <Descriptions.Item label="End Time">{fmt(exec.end_time)}</Descriptions.Item>
          <Descriptions.Item label="Started By">{personName(exec.start_employee)}</Descriptions.Item>
          <Descriptions.Item label="Ended By">{personName(exec.end_employee)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {durationDeviation && (
        <Card size="small" title="Duration Deviation" style={{ borderLeft: '4px solid #dc2626' }}>
          <Descriptions size="small" column={3}>
            {Object.entries(durationDeviation).map(([k, v]) => (
              <Descriptions.Item label={k.replace(/_/g, ' ')} key={k}>
                {v == null || v === '' ? '—' : String(v)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </Card>
      )}

      {supervisorApproval && (
        <Card size="small" title="Supervisor Approval">
          <Descriptions size="small" column={3}>
            <Descriptions.Item label="Status">
              <Tag color={statusTagColor(supervisorApproval.status)}>{supervisorApproval.status || '—'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Ticket">{supervisorApproval.ticket_id ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Requested By">{personName(supervisorApproval.requested_by)}</Descriptions.Item>
            <Descriptions.Item label="Requested At">{fmt(supervisorApproval.requested_at)}</Descriptions.Item>
            <Descriptions.Item label="Reviewed By">{personName(supervisorApproval.reviewed_by)}</Descriptions.Item>
            <Descriptions.Item label="Reviewed At">{fmt(supervisorApproval.reviewed_at)}</Descriptions.Item>
            <Descriptions.Item label="Comment" span={2}>
              {supervisorApproval.review_comment || '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card size="small" title={`Recipe Approvals (${recipeApprovals.length})`}>
        {recipeApprovals.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recipe approvals" />
        ) : (
          <div className="space-y-1">
            {recipeApprovals.map((a, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 border-b pb-1" style={{ borderColor: '#f0f0f0' }}>
                <Tag color="blue">Seq {a.sequence ?? '—'}</Tag>
                <b>{a.role || '—'}</b>
                <Tag color={statusTagColor(a.status)}>{a.status || '—'}</Tag>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {personName(a.employee)} · {fmt(a.review_datetime)}
                </span>
                {a.comment && <span style={{ fontSize: 12 }}>“{a.comment}”</span>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );

  const ingredientCols = [
    { title: 'Code', dataIndex: 'ingredient_code', key: 'ingredient_code', render: (v) => v || '—' },
    { title: 'Type', dataIndex: 'ingredient_type', key: 'ingredient_type', render: (v) => <Tag>{v || '—'}</Tag> },
    {
      title: 'Material',
      key: 'material',
      render: (_, r) => (r.material_names || []).join(', ') || '—',
    },
    {
      title: 'Unique ID',
      key: 'unique_id',
      render: (_, r) => (r.material_unique_ids || []).join(', ') || '—',
    },
    { title: 'Expected Qty', dataIndex: 'expected_quantity', key: 'expected_quantity', render: (v, r) => (v != null ? `${v}${r.unit ? ` ${r.unit}` : ''}` : '—') },
    { title: 'Deviation %', dataIndex: 'deviation_pct', key: 'deviation_pct', render: (v) => (v != null ? `${v}%` : '—') },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' },
    { title: 'Execution Mode', dataIndex: 'execution_mode', key: 'execution_mode', render: (v) => v || '—' },
    { title: 'Kitting', key: 'kitting', render: (_, r) => (r.is_kitting ? yesNo(r.kitting_done) : '—') },
    { title: 'Instruction', dataIndex: 'instruction', key: 'instruction', render: (v) => v || '—' },
  ];

  const parameterCols = [
    { title: 'Code', dataIndex: 'parameter_code', key: 'parameter_code', render: (v) => v || '—' },
    { title: 'Name', dataIndex: 'parameter_name', key: 'parameter_name', render: (v) => v || '—' },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' },
    {
      title: 'Range',
      key: 'range',
      render: (_, r) => `${r.min_value ?? '—'} – ${r.max_value ?? '—'}`,
    },
    { title: 'Readings', dataIndex: 'readings', key: 'readings', render: (v) => v ?? '—' },
    { title: 'Frequency', dataIndex: 'frequency', key: 'frequency', render: (v) => v ?? '—' },
    {
      title: 'Flags',
      key: 'flags',
      render: (_, r) => (
        <span className="flex gap-1">
          {r.is_start && <Tag color="blue">Start</Tag>}
          {r.is_end && <Tag color="purple">End</Tag>}
          {r.is_continues && <Tag color="cyan">Continuous</Tag>}
        </span>
      ),
    },
  ];

  const samplingCols = [
    { title: 'Sample No.', dataIndex: 'sample_number', key: 'sample_number', render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag color={statusTagColor(v)}>{v || '—'}</Tag> },
    {
      title: 'Result',
      dataIndex: 'sampling_result',
      key: 'sampling_result',
      render: (v) => (v ? <Tag color={statusTagColor(v)}>{v}</Tag> : '—'),
    },
    { title: 'Passed', dataIndex: 'sampling_status', key: 'sampling_status', align: 'center', render: yesNo },
    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', render: (v) => v ?? '—' },
    { title: 'Testing Type', dataIndex: 'testing_type', key: 'testing_type', render: (v) => v || '—' },
    { title: 'Observation', dataIndex: 'observation', key: 'observation', render: (v) => v || '—' },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', render: (v) => v || '—' },
    { title: 'Stage', dataIndex: 'operation_stage', key: 'operation_stage', render: (v) => v || '—' },
    { title: 'Recorded By', dataIndex: 'recorded_by', key: 'recorded_by', render: personName },
  ];

  const productCols = [
    { title: 'Product', dataIndex: 'product_name', key: 'product_name', render: (v) => v || '—' },
    { title: 'Theoretical Qty', dataIndex: 'theoretical_quantity', key: 'theoretical_quantity', render: (v) => v ?? '—' },
    {
      title: 'Expected Range',
      key: 'expected_range',
      render: (_, r) => `${r.expected_min ?? '—'} – ${r.expected_max ?? '—'}`,
    },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' },
    { title: 'Instruction', dataIndex: 'instruction', key: 'instruction', render: (v) => v || '—' },
  ];

  const instructionCols = [
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' },
    { title: 'Instruction', dataIndex: 'instruction', key: 'instruction', render: (v) => v || '—' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: yesNo },
    { title: 'Rejection Reason', dataIndex: 'rejection_reason', key: 'rejection_reason', render: (v) => v || '—' },
  ];

  const actionListCols = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v) => v || '—' },
    { title: 'Label', dataIndex: 'label', key: 'label', render: (v) => v || '—' },
    { title: 'Data Type', dataIndex: 'data_type', key: 'data_type', render: (v) => v || '—' },
    { title: 'Field Name', dataIndex: 'field_name', key: 'field_name', render: (v) => v || '—' },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' },
    { title: 'Actual Value', dataIndex: 'actual_value', key: 'actual_value', render: (v) => v ?? '—' },
    { title: 'Selected Button', dataIndex: 'selected_button', key: 'selected_button', render: (v) => v || '—' },
    { title: 'Comment', dataIndex: 'comment', key: 'comment', render: (v) => v || '—' },
  ];

  const lineClearanceCols = [
    { title: 'Type', dataIndex: 'clearance_type', key: 'clearance_type', render: (v) => v || '—' },
    { title: 'Instruction', dataIndex: 'instruction', key: 'instruction', render: (v) => v || '—' },
    { title: 'Expected', dataIndex: 'expected_value', key: 'expected_value', render: (v) => v || '—' },
    { title: 'Actual', dataIndex: 'actual_value', key: 'actual_value', render: (v) => v || '—' },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' },
    { title: 'Rejection Reason', dataIndex: 'rejection_reason', key: 'rejection_reason', render: (v) => v || '—' },
    { title: 'Rejected By', dataIndex: 'rejected_by', key: 'rejected_by', render: (v) => v || '—' },
  ];

  const deviationCols = [
    { title: 'Type', dataIndex: 'type', key: 'type', render: (v) => <Tag>{v || '—'}</Tag> },
    { title: 'Deviation', dataIndex: 'deviation_type', key: 'deviation_type', render: (v) => <Tag color="red">{v || '—'}</Tag> },
    {
      title: 'Item',
      key: 'item',
      render: (_, r) => r.ingredient || r.parameter || '—',
    },
    { title: 'Actual', dataIndex: 'actual_value', key: 'actual_value', render: (v) => v ?? '—' },
    { title: 'Expected', dataIndex: 'expected_value', key: 'expected_value', render: (v) => v ?? '—' },
    {
      title: 'Range',
      key: 'range',
      render: (_, r) => (r.min_value != null || r.max_value != null ? `${r.min_value ?? '—'} – ${r.max_value ?? '—'}` : '—'),
    },
    { title: 'Action', dataIndex: 'action', key: 'action', render: (v) => <Tag color="orange">{v || '—'}</Tag> },
    { title: 'Created On', dataIndex: 'created_on', key: 'created_on', render: fmt },
    { title: 'Ticket', dataIndex: 'ticket', key: 'ticket', render: (t) => <TicketTag ticket={t} /> },
  ];

  const esignCols = [
    { title: 'Action', dataIndex: 'action_type', key: 'action_type', render: (v) => v || '—' },
    { title: 'Category', dataIndex: 'category', key: 'category', render: (v) => v || '—' },
    { title: 'Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' },
    { title: 'Done By', dataIndex: 'done_by', key: 'done_by', render: personName },
    { title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp', render: fmt },
    { title: 'User Verified', dataIndex: 'is_user_verified', key: 'is_user_verified', render: yesNo },
    { title: 'Cert Verified', dataIndex: 'is_verified_by_certificate', key: 'is_verified_by_certificate', render: yesNo },
  ];

  const renderTable = (rows, cols, emptyText, expandable) => (
    <Table
      size="small"
      dataSource={(rows || []).map((r, i) => ({ ...r, key: r.actual_id ?? r.deviation_id ?? i }))}
      columns={cols}
      pagination={false}
      scroll={{ x: 'max-content' }}
      expandable={expandable}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} /> }}
    />
  );

  const withActualsExpand = (extraCols) => ({
    expandedRowRender: (record) => (
      <div>
        {renderActualsTable(record.actuals, extraCols)}
        {renderDeviationBlock(record.deviation)}
      </div>
    ),
    rowExpandable: (record) => (record.actuals?.length > 0) || !!record.deviation,
  });

  const items = [
    { key: 'overview', label: iconLabel(<Icons.FileText size={14} />, 'Overview'), children: overviewTab },
    {
      key: 'ingredients',
      label: iconLabel(<TestTube size={14} />, `Ingredients (${ingredients.length})`),
      children: renderTable(
        ingredients,
        ingredientCols,
        'No ingredients',
        withActualsExpand([{ title: 'Quantity', dataIndex: 'quantity', key: 'quantity', render: (v) => v ?? '—' }, { title: 'Lot No.', dataIndex: 'lot_number', key: 'lot_number', render: (v) => v || '—' }, { title: 'Temp', dataIndex: 'temperature', key: 'temperature', render: (v) => v ?? '—' }])
      ),
    },
    {
      key: 'parameters',
      label: iconLabel(<Icons.SlidersHorizontal size={14} />, `Parameters (${parameters.length})`),
      children: renderTable(
        parameters,
        parameterCols,
        'No parameters',
        withActualsExpand([{ title: 'Reading Stage', dataIndex: 'stage', key: 'stage', render: (v) => v || '—' }, { title: 'Value', dataIndex: 'value', key: 'value', render: (v) => v ?? '—' }, { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', render: (v) => v || '—' }])
      ),
    },
    {
      key: 'sampling',
      label: iconLabel(<Icons.FlaskConical size={14} />, `Sampling (${sampling.length})`),
      children: renderTable(sampling, samplingCols, 'No sampling records', {
        expandedRowRender: (record) => renderDeviationBlock(record.deviation),
        rowExpandable: (record) => !!record.deviation,
      }),
    },
    {
      key: 'products',
      label: iconLabel(<Icons.Package size={14} />, `Products (${products.length})`),
      children: renderTable(
        products,
        productCols,
        'No products',
        withActualsExpand([{ title: 'Container', dataIndex: 'container_id', key: 'container_id', render: (v) => v || '—' }, { title: 'Tare Wt.', dataIndex: 'tare_weight', key: 'tare_weight', render: (v) => v ?? '—' }, { title: 'Gross Wt.', dataIndex: 'gross_weight', key: 'gross_weight', render: (v) => v ?? '—' }, { title: 'Net Wt.', dataIndex: 'net_weight', key: 'net_weight', render: (v) => v ?? '—' }, { title: 'Temp', dataIndex: 'temperature', key: 'temperature', render: (v) => v ?? '—' }])
      ),
    },
    {
      key: 'instructions',
      label: iconLabel(<Icons.ListChecks size={14} />, `Instructions (${instructions.length})`),
      children: renderTable(instructions, instructionCols, 'No instructions'),
    },
    {
      key: 'action_list',
      label: iconLabel(<Icons.CheckSquare size={14} />, `Action List (${actionList.length})`),
      children: renderTable(actionList, actionListCols, 'No actions'),
    },
    {
      key: 'line_clearance',
      label: iconLabel(<Icons.ShieldCheck size={14} />, `Line Clearance (${lineClearances.length})`),
      children: renderTable(lineClearances, lineClearanceCols, 'No line clearance records'),
    },
    {
      key: 'deviations',
      label: iconLabel(<ShieldAlert size={14} />, `Deviations (${deviations.length})`),
      children: renderTable(deviations, deviationCols, 'No deviations'),
    },
    {
      key: 'esign',
      label: iconLabel(<PenLine size={14} />, `E-Sign Audit (${esignAudit.length})`),
      children: renderTable(esignAudit, esignCols, 'No e-sign events'),
    },
    {
      key: 'reviews_notes',
      label: iconLabel(<StickyNote size={14} />, 'Reviews & Notes'),
      children: (
        <div className="space-y-4">
          <Card size="small" title={`Reviews (${reviews.length})`}>
            {reviews.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No reviews" />
            ) : (
              <div className="space-y-2">
                {reviews.map((r, i) => (
                  <div key={r.review_id ?? i} className="border-b pb-2" style={{ borderColor: '#f0f0f0' }}>
                    <Rate disabled defaultValue={r.rating || 0} style={{ fontSize: 14 }} />
                    <div style={{ fontSize: 13, color: textColor }}>{r.review}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card size="small" title={`Notes (${notes.length})`}>
            {notes.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes" />
            ) : (
              <ul className="list-disc pl-5 space-y-1">
                {notes.map((n, i) => (
                  <li key={n.note_id ?? i} style={{ fontSize: 13, color: textColor }}>
                    {n.note}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      ),
    },
  ];

  return (
    <Card size="small">
      <style>{`
        .op-detail-tabs > .ant-tabs-nav {
          position: sticky;
          top: 0;
          z-index: 10;
          background: ${isDark ? '#1f1f1f' : '#ffffff'};
          margin-bottom: 12px;
        }
      `}</style>
      <Tabs items={items} className="op-detail-tabs" />
    </Card>
  );
};

OperationDetailPanel.propTypes = {
  batchNumber: PropTypes.string,
  operationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default OperationDetailPanel;
